import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';

import { copyProducedText } from '../Esign/clipboard';
import { useToasts } from '../Toast/useToasts';
import { leadErrorKey, leadSmsReasonKey } from './leadsModel';

/**
 * ТЗ 0084 — тосты и тексты ошибок раздела «Лиды» одним местом. Перевод — по
 * коду (`leads.errors.<CODE>`), незнакомый код — текст сервера как есть: он
 * лучше пустоты или сырого кода.
 */

export interface ILeadError {
  /** Поле формы (`mailbox_email`, `subject`, `text`…); пусто — общая. */
  field: string;
  code: string;
  message: string;
}

/** Текст одной ошибки: перевод кода, иначе сообщение сервера, иначе код. */
export const useLeadErrorText = () => {
  const { t } = useTranslation();
  return useCallback(
    (
      error:
        | { code?: string | null; message?: string | null }
        | null
        | undefined,
    ): string => {
      if (!error) return '';
      const code = (error.code || '').trim();
      const message = (error.message || '').trim();
      const key = leadErrorKey(code);
      if (key) return t(key, { defaultValue: message || code });
      return message || code || t('leads.errors.unknown');
    },
    [t],
  );
};

/**
 * Перепроверка 0084: почему SMS лиду не уходит — словами продавца
 * (`leads.smsReasons`), одним текстом на код везде в карточке лида.
 * Незнакомый код — `''`: вызывающий берёт общие слова SMS.
 */
export const useLeadSmsReasonText = () => {
  const { t } = useTranslation();
  return useCallback(
    (reason: string | null | undefined): string => {
      const key = leadSmsReasonKey(reason);
      return key ? t(key) : '';
    },
    [t],
  );
};

/** Причина без точки в конце — чтобы её можно было вставить во фразу. */
export const withoutFinalDot = (text: string): string =>
  text.trim().replace(/[.。]+$/, '');

/**
 * Причина-предложение («Лид закрыт…») как часть фразы («Сейчас не шлёт:
 * лид закрыт…»): без точки и со строчной буквы. Аббревиатуры («SMS
 * выключены») не трогаем — у них вторая буква тоже заглавная.
 */
export const asClause = (text: string): string => {
  const clause = withoutFinalDot(text);
  return /^[A-ZА-ЯЁĄĆĘŁŃÓŚŹŻ][a-zа-яёąćęłńóśźż]/.test(clause)
    ? clause.charAt(0).toLowerCase() + clause.slice(1)
    : clause;
};

/** Тост раздела — свой тип: тосты дедуплицируются по типу. */
export const useLeadsNotify = () => {
  const { addToast } = useToasts();
  return useCallback(
    (type: ToastTypes, title: string, text?: string) => {
      addToast({
        data: { type, title, text },
        type: GlobalToastTypeNames.Leads,
      });
    },
    [addToast],
  );
};

/** Скопировать телефон или email лида в буфер — с тостом «скопировано». */
export const useLeadCopy = () => {
  const { t } = useTranslation();
  const notify = useLeadsNotify();
  return useCallback(
    async (value: string): Promise<boolean> => {
      const outcome = await copyProducedText(async () => value || null);
      if (outcome === 'copied') {
        notify(ToastTypes.success, t('leads.toast.copied'), value);
        return true;
      }
      notify(ToastTypes.error, t('leads.toast.copyFailed'));
      return false;
    },
    [notify, t],
  );
};
